<script setup>
/**
 * 上映スケジュールAI ステップ2：作品の実績パネル（DEV-14982）
 *
 * - 観客属性：全国の SNS 推定値（DP には地域別の年齢・性別データがないため「全国・SNS推定」を必ず表記）
 * - 地域充填率：本館所在の都道府県 / 地方の上映回数・劇場数・充填率
 * - 本館実績：本館自身の上映回数・動員・座席数・稼働率
 * 各ブロックは hasData=false のとき「データなし」を表示する。
 */
import { ref, computed, watch } from "vue";
import AudiencePieChart from "@/views/dp/client-v2/components/audience-pie-chart.vue";
import { getScheduleAiMovieInsightService } from "@/api/dp/index.js";

// 観客属性の口径表記（API の scope が空でも必ず表示する）
const AUDIENCE_SCOPE = "全国・SNS推定";
// 年齢層の表示順（audience-pie-chart と同じ）
const AGE_ORDER = ["10代", "20代", "30代", "40代", "50代", "60代+"];

const props = defineProps({
  movieId: { type: [Number, String], required: true },
});

const loading = ref(false);
const loadError = ref(false);
const insight = ref(null);

// 古いリクエストの結果で上書きしないための連番
let fetchSeq = 0;
const fetchInsight = async () => {
  if (props.movieId == null || props.movieId === "") return;
  const seq = ++fetchSeq;
  loading.value = true;
  loadError.value = false;
  try {
    const res = await getScheduleAiMovieInsightService(props.movieId);
    if (seq !== fetchSeq) return;
    insight.value = res?.data || null;
  } catch {
    if (seq !== fetchSeq) return;
    // エラーメッセージは request.js でトースト済み
    loadError.value = true;
    insight.value = null;
  } finally {
    if (seq === fetchSeq) loading.value = false;
  }
};

watch(() => props.movieId, fetchInsight, { immediate: true });

const audience = computed(() => insight.value?.audience || null);
const regionFill = computed(() => insight.value?.regionFill || null);
const cinemaPerformance = computed(() => insight.value?.cinemaPerformance || null);

// 円グラフに渡す観客属性（genders/ageGroups は /hotword/audienceProfile と同じ構造）
const audienceProfile = computed(() => ({
  genders: audience.value?.genders || [],
  ageGroups: audience.value?.ageGroups || [],
}));

// 年齢層バー（最大値を 100% 幅として相対表示）
const ageBars = computed(() => {
  const list = (audience.value?.ageGroups || [])
    .map((a) => ({ ageGroup: String(a?.ageGroup ?? ""), percent: Number(a?.percent ?? 0) }))
    .sort((a, b) => {
      const ia = AGE_ORDER.indexOf(a.ageGroup);
      const ib = AGE_ORDER.indexOf(b.ageGroup);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  const max = Math.max(0, ...list.map((a) => a.percent));
  return list.map((a) => ({ ...a, width: max > 0 ? (a.percent / max) * 100 : 0 }));
});

/**
 * 数値を 3 桁区切りで表示（null は「-」）
 */
const formatNumber = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString());

/**
 * 0〜100 の比率を「12.3%」形式で表示（null は「-」）
 */
const formatPercent = (value) => (value === null || value === undefined ? "-" : `${Number(value).toFixed(1)}%`);

/**
 * データ期間の表示。片方しかなければある方だけ
 */
const formatPeriod = (block) => {
  if (!block?.dataFrom && !block?.dataTo) return "";
  return `${block.dataFrom || ""} 〜 ${block.dataTo || ""}`;
};

// 地域充填率のカード（都道府県・地方）
const regionCards = computed(() => [
  { key: "prefecture", label: "都道府県", data: regionFill.value?.prefecture },
  { key: "area", label: "地方", data: regionFill.value?.area },
]);

defineExpose({ reload: fetchInsight });
</script>

<template>
  <div v-loading="loading" class="insight">
    <div v-if="loadError && !loading" class="insight-error">
      実績データの取得に失敗しました
      <el-button link type="primary" @click="fetchInsight">再読み込み</el-button>
    </div>

    <div v-else class="insight-grid">
      <!-- 観客属性（全国・SNS推定） -->
      <section class="insight-block insight-block--audience">
        <header class="insight-head">
          <h4>観客属性</h4>
          <span class="scope-tag">{{ AUDIENCE_SCOPE }}</span>
        </header>
        <p v-if="!audience?.hasData" class="no-data">データなし</p>
        <div v-else class="audience-body">
          <div class="audience-chart">
            <AudiencePieChart :profile="audienceProfile" initial-view="gender" />
          </div>
          <div class="age-bars">
            <div class="age-bars__title">年齢層</div>
            <div v-for="a in ageBars" :key="a.ageGroup" class="age-bar">
              <span class="age-bar__label">{{ a.ageGroup }}</span>
              <span class="age-bar__track">
                <span class="age-bar__fill" :style="{ width: `${a.width}%` }" />
              </span>
              <span class="age-bar__value">{{ formatPercent(a.percent) }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 地域充填率 -->
      <section class="insight-block">
        <header class="insight-head">
          <h4>地域の充填率</h4>
          <span v-if="regionFill?.hasData && formatPeriod(regionFill)" class="period">{{ formatPeriod(regionFill) }}</span>
        </header>
        <p v-if="!regionFill?.hasData" class="no-data">データなし</p>
        <div v-else class="stat-cards">
          <div v-for="c in regionCards" :key="c.key" class="stat-card">
            <div class="stat-card__title">
              <span class="stat-card__kind">{{ c.label }}</span>
              {{ c.data?.name || "-" }}
            </div>
            <dl class="stat-list">
              <div>
                <dt>上映回数</dt>
                <dd>{{ formatNumber(c.data?.scheduleCount) }}</dd>
              </div>
              <div>
                <dt>劇場数</dt>
                <dd>{{ formatNumber(c.data?.cinemaCount) }}</dd>
              </div>
              <div>
                <dt>充填率</dt>
                <dd class="em">{{ formatPercent(c.data?.fillRate) }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <!-- 本館実績 -->
      <section class="insight-block">
        <header class="insight-head">
          <h4>本館の実績</h4>
          <span v-if="cinemaPerformance?.hasData && formatPeriod(cinemaPerformance)" class="period">{{ formatPeriod(cinemaPerformance) }}</span>
        </header>
        <p v-if="!cinemaPerformance?.hasData" class="no-data">データなし</p>
        <dl v-else class="stat-list stat-list--grid">
          <div>
            <dt>上映回数</dt>
            <dd>{{ formatNumber(cinemaPerformance.scheduleCount) }}</dd>
          </div>
          <div>
            <dt>動員</dt>
            <dd>{{ formatNumber(cinemaPerformance.occupiedSeats) }}</dd>
          </div>
          <div>
            <dt>座席数</dt>
            <dd>{{ formatNumber(cinemaPerformance.seatTotal) }}</dd>
          </div>
          <div>
            <dt>稼働率</dt>
            <dd class="em">{{ formatPercent(cinemaPerformance.occupancyRate) }}</dd>
          </div>
        </dl>
      </section>
    </div>
  </div>
</template>

<style lang="less" scoped>
/* 親ページ（movie/index.vue）の --sp-* トークンを継承して使う */
.insight {
  min-height: 80px;
  padding: 4px 0;
}

.insight-error {
  padding: 16px;
  font-size: 13px;
  color: var(--sp-muted);
}

.insight-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
}

.insight-block {
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid var(--sp-border);
  border-radius: var(--sp-radius);
  background: var(--sp-panel);
}

.insight-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-bottom: 10px;

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
  }
}

.scope-tag {
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 18px;
  color: var(--sp-brand);
  background: var(--sp-tint-strong);
}

.period {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--sp-muted);
}

.no-data {
  margin: 0;
  padding: 24px 0;
  font-size: 13px;
  text-align: center;
  color: var(--sp-muted);
}

/* ---------- 観客属性 ---------- */
.audience-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.audience-chart {
  min-width: 0;

  // 実績パネル内では円グラフを小さめに
  :deep(.audience-chart-wrap) {
    height: 220px;
  }
}

.age-bars {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &__title {
    font-size: 12px;
    font-weight: 700;
    color: var(--sp-muted);
  }
}

.age-bar {
  display: grid;
  grid-template-columns: 44px 1fr 52px;
  align-items: center;
  gap: 8px;
  font-size: 12px;

  &__label {
    color: var(--sp-text-2);
  }

  &__track {
    height: 10px;
    overflow: hidden;
    border-radius: 999px;
    background: color-mix(in srgb, var(--sp-muted) 10%, var(--sp-panel));
  }

  &__fill {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: var(--sp-brand);
  }

  &__value {
    font-weight: 600;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
}

/* ---------- 実績数値 ---------- */
.stat-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stat-card {
  &__title {
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 600;
  }

  &__kind {
    margin-right: 6px;
    font-size: 11px;
    font-weight: 400;
    color: var(--sp-muted);
  }

  & + & {
    padding-top: 10px;
    border-top: 1px solid var(--sp-border);
  }
}

.stat-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px 12px;
  margin: 0;

  dt {
    font-size: 11px;
    color: var(--sp-muted);
  }

  dd {
    margin: 2px 0 0;
    font-size: 15px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;

    &.em {
      color: var(--sp-brand);
    }
  }

  &--grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;

    dd {
      font-size: 18px;
    }
  }
}

@media (max-width: 1100px) {
  .insight-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .insight-block--audience {
    grid-column: 1 / -1;
  }
}

@media (max-width: 768px) {
  .insight-grid,
  .audience-body {
    grid-template-columns: 1fr;
  }
}
</style>
